import { SparklesIcon } from 'lucide-react';
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';

import {
  parseBoolean,
  parseNumber,
  useStoredPreference,
} from '@/features/diagrams/useStoredPreference.js';
import { cn } from '@/lib/utils.js';

/** 18rem: below this the chat's own controls start to wrap. */
export const MIN_CHAT_WIDTH = 288;
/** 26rem. */
export const DEFAULT_CHAT_WIDTH = 416;
/** How far one arrow key moves the handle, in pixels. */
const KEY_STEP = 16;
/** The padding around the layout when it covers the screen, in pixels (0.75rem), which the handle measures past. */
const OVERLAY_GAP = 12;

/** The chat width, kept between 18rem and half the page. A page too narrow for both keeps 18rem. */
export function clampChatWidth(width: number, pageWidth: number): number {
  const widest = Math.max(MIN_CHAT_WIDTH, pageWidth / 2);
  return Math.round(Math.min(Math.max(width, MIN_CHAT_WIDTH), widest));
}

/** The browser keys one host keeps its Assistant's width and collapse under. */
export interface AssistantPreferenceKeys {
  width: string;
  collapsed: string;
}

/** The Assistant's remembered width and whether it is collapsed to its rail. */
export interface AssistantPanelState {
  width: number;
  setWidth: (width: number) => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

/**
 * The Assistant's width and collapse, kept in this browser under the host's own keys, so each host
 * remembers its own and a saved width survives the move between hosts.
 */
export function useAssistantPanelState(
  keys: AssistantPreferenceKeys,
  defaultCollapsed = false,
): AssistantPanelState {
  const [width, setWidth] = useStoredPreference(
    keys.width,
    DEFAULT_CHAT_WIDTH,
    parseNumber,
  );
  const [collapsed, setCollapsed] = useStoredPreference(
    keys.collapsed,
    defaultCollapsed,
    parseBoolean,
  );
  return { width, setWidth, collapsed, setCollapsed };
}

/**
 * A document beside its Assistant, for any host: the diagram editor, the file viewer. On a wide
 * screen a handle between them sets the chat's width, by dragging or with the arrow keys, and the chat
 * can collapse to a slim Assistant rail. Collapsing only hides the chat, so a reply that is still
 * running keeps running. Below `lg` the two stack and the handle is not shown.
 *
 * The host owns the width and collapse ({@link useAssistantPanelState}), so its own controls, such as
 * the Assistant button, read and change the same state. `fitParent` makes the layout fit a bounded
 * box, a dialog or the whole screen, instead of giving the document a tall minimum. `overlayClassName`
 * is for a host that lays the layout over the whole page; the handle then measures past its padding.
 * The chat keeps its place in the tree in every mode and only its classes change, so a running reply
 * is never cut off by switching.
 */
export function AssistantLayout({
  containerRef,
  main,
  chat,
  panel,
  fitParent = false,
  overlayClassName,
  panelClassName,
  fixedWidth,
}: {
  containerRef: RefObject<HTMLDivElement | null>;
  main: ReactNode;
  /** The chat panel, given the function that collapses it. Null shows the document alone, with no handle or rail. */
  chat: ((collapse: () => void) => ReactNode) | null;
  panel: AssistantPanelState;
  fitParent?: boolean;
  fixedWidth?: number;
  /** Classes for covering the whole page, or nothing when the layout sits in the page. */
  overlayClassName?: string;
  /** Extra classes for the chat's box, such as a height when the host does not fit a bounded parent. */
  panelClassName?: string;
}): JSX.Element {
  const pageWidth = usePageWidth(containerRef);
  const width = fixedWidth ?? clampChatWidth(panel.width, pageWidth);
  const resize = (next: number): void =>
    panel.setWidth(clampChatWidth(next, pageWidth));
  const edgeGap = overlayClassName ? OVERLAY_GAP : 0;
  const mainArea = (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col gap-2',
        fitParent ? 'min-h-0' : 'min-h-[60vh]',
      )}
    >
      {main}
    </div>
  );
  if (!chat) {
    return (
      <div
        ref={containerRef}
        className={cn(
          'flex min-h-0 flex-1 flex-col',
          overlayClassName && `${overlayClassName} p-3`,
        )}
      >
        {mainArea}
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-0',
        overlayClassName && `${overlayClassName} gap-2 p-3`,
      )}
      style={{ '--chat-width': `${width}px` } as CSSProperties}
    >
      {mainArea}
      {panel.collapsed ? (
        <AssistantRail onExpand={() => panel.setCollapsed(false)} />
      ) : fixedWidth ? (
        <div className="hidden w-5 shrink-0 lg:block" />
      ) : (
        <ResizeHandle
          width={width}
          widest={
            Number.isFinite(pageWidth)
              ? clampChatWidth(Infinity, pageWidth)
              : undefined
          }
          onResize={resize}
          rightEdge={() =>
            (containerRef.current?.getBoundingClientRect().right ?? 0) - edgeGap
          }
        />
      )}
      <div
        data-assistant-panel=""
        hidden={panel.collapsed}
        className={cn(
          'min-h-0 lg:w-[var(--chat-width)] lg:shrink-0',
          fitParent && 'h-[45vh] lg:h-auto',
          panelClassName,
        )}
      >
        {chat(() => panel.setCollapsed(true))}
      </div>
    </div>
  );
}

/** The collapsed Assistant: a slim rail that expands it again. */
function AssistantRail({ onExpand }: { onExpand: () => void }): JSX.Element {
  return (
    <button
      type="button"
      aria-label="Show assistant"
      title="Show assistant"
      onClick={onExpand}
      className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-2 py-2 text-sm text-muted-foreground hover:text-foreground lg:ml-2 lg:w-9 lg:flex-col lg:py-4"
    >
      <SparklesIcon aria-hidden className="size-4 text-primary" />
      <span className="lg:[writing-mode:vertical-rl]">Assistant</span>
    </button>
  );
}

/**
 * The layout's own width, measured before the first paint and again on every resize, so a width
 * remembered on a wide screen is clamped to the page that is actually shown. Infinity until the
 * element exists, which leaves only the minimum in force.
 */
function usePageWidth(container: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(Infinity);
  useLayoutEffect(() => {
    const element = container.current;
    if (!element) return undefined;
    const measure = (): void => setWidth(element.clientWidth || Infinity);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [container]);
  return width;
}

/** The handle between the document and the chat: drag it, or move it with the arrow keys. */
function ResizeHandle({
  width,
  widest,
  onResize,
  rightEdge,
}: {
  width: number;
  /** The widest the chat may be on this page, once the page is measured. */
  widest: number | undefined;
  onResize: (width: number) => void;
  rightEdge: () => number;
}): JSX.Element {
  const dragging = useRef(false);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowLeft') onResize(width + KEY_STEP);
    else if (event.key === 'ArrowRight') onResize(width - KEY_STEP);
    else return;
    event.preventDefault();
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    dragging.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (dragging.current) onResize(rightEdge() - event.clientX);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the assistant"
      aria-valuenow={width}
      aria-valuemin={MIN_CHAT_WIDTH}
      aria-valuemax={widest}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => {
        dragging.current = false;
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
      className="group hidden w-4 shrink-0 cursor-col-resize touch-none items-stretch justify-center outline-none lg:flex"
    >
      <span className="w-px bg-border transition-colors group-hover:bg-primary group-focus-visible:w-0.5 group-focus-visible:bg-primary" />
    </div>
  );
}
